import React from 'react';
import { PercentIcon, TrendingUpIcon, CalendarIcon, SaveIcon } from 'lucide-react';
export function InterestRateModule({
  role
}: {
  role: string;
}) {
  const rateHistory = [{
    id: 1,
    rate: '10.5%',
    benchmark: '90-day T-Bill (12.5%)',
    effectiveFrom: '2026-01-01',
    effectiveTo: 'Present',
    updatedBy: 'System'
  }, {
    id: 2,
    rate: '9.0%',
    benchmark: '90-day T-Bill (11.0%)',
    effectiveFrom: '2025-07-01',
    effectiveTo: '2025-12-31',
    updatedBy: 'Adewale O.'
  }, {
    id: 3,
    rate: '8.5%',
    benchmark: '90-day T-Bill (10.0%)',
    effectiveFrom: '2025-01-01',
    effectiveTo: '2025-06-30',
    updatedBy: 'System'
  }];
  return <div className="space-y-6" data-id="element-358">
      <div className="flex justify-between items-center" data-id="element-359">
        <div data-id="element-360">
          <h2 className="text-2xl font-bold text-[#0D3B3B]" data-id="element-361">
            Interest Rate Manager
          </h2>
          <p className="text-sm text-gray-500" data-id="element-362">
            Manage variable interest rates for GoalGuard savings products.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-363">
        {/* Current Rate Card */}
        <div className="lg:col-span-1 bg-gradient-to-br from-[#0D3B3B] to-[#1a5c5c] rounded-3xl p-6 text-white shadow-xl shadow-[#0D3B3B]/20 relative overflow-hidden" data-id="element-364">
          <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full blur-3xl -mr-10 -mt-10 pointer-events-none" data-id="element-365" />

          <div className="relative z-10" data-id="element-366">
            <div className="flex items-center gap-3 mb-6" data-id="element-367">
              <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center backdrop-blur-sm border border-white/10" data-id="element-368">
                <PercentIcon className="w-6 h-6 text-[#FFD700]" data-id="element-369" />
              </div>
              <div data-id="element-370">
                <p className="text-white/60 text-xs font-bold uppercase tracking-wider" data-id="element-371">
                  Current Rate
                </p>
                <p className="text-sm font-medium text-emerald-400" data-id="element-372">
                  GoalGuard
                </p>
              </div>
            </div>

            <h1 className="text-6xl font-black tracking-tight mb-2" data-id="element-373">10.5%</h1>

            <div className="space-y-3 mt-8" data-id="element-374">
              <div className="flex justify-between items-center text-sm border-b border-white/10 pb-2" data-id="element-375">
                <span className="text-white/60" data-id="element-376">Benchmark</span>
                <span className="font-bold" data-id="element-377">90-day T-Bill (12.5%)</span>
              </div>
              <div className="flex justify-between items-center text-sm border-b border-white/10 pb-2" data-id="element-378">
                <span className="text-white/60" data-id="element-379">Effective Since</span>
                <span className="font-bold" data-id="element-380">Jan 1, 2026</span>
              </div>
              <div className="flex justify-between items-center text-sm" data-id="element-381">
                <span className="text-white/60" data-id="element-382">Affected Policies</span>
                <span className="font-bold text-[#FFD700]" data-id="element-383">12,450</span>
              </div>
            </div>
          </div>
        </div>

        {/* Update Rate Form (Super Admin Only) */}
        <div className="lg:col-span-2 bg-white rounded-3xl border border-gray-100 shadow-sm p-6" data-id="element-384">
          <h3 className="font-bold text-[#0D3B3B] text-lg mb-6 flex items-center gap-2" data-id="element-385">
            <TrendingUpIcon className="w-5 h-5 text-[#2BA89D]" data-id="element-386" /> Update
            Interest Rate
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-387">
            <div data-id="element-388">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-389">
                New Interest Rate (%)
              </label>
              <div className="relative" data-id="element-390">
                <PercentIcon className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" data-id="element-391" />
                <input type="number" step="0.1" placeholder="e.g. 11.0" disabled={role !== 'Super Admin'} className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-bold text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#0D3B3B] disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-392" />
              </div>
            </div>
            <div data-id="element-393">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-394">
                Benchmark Reference
              </label>
              <input type="text" placeholder="e.g. 90-day T-Bill (13.0%)" disabled={role !== 'Super Admin'} className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-bold text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#0D3B3B] disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-395" />
            </div>
            <div className="md:col-span-2" data-id="element-396">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-397">
                Effective Date
              </label>
              <div className="relative" data-id="element-398">
                <CalendarIcon className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" data-id="element-399" />
                <input type="date" disabled={role !== 'Super Admin'} className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-bold text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#0D3B3B] disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-400" />
              </div>
              <p className="text-xs text-gray-500 mt-2" data-id="element-401">
                The new rate will automatically apply to all active GoalGuard
                plans from this date forward. Past calculations remain
                unaffected.
              </p>
            </div>
          </div>

          <div className="flex justify-end" data-id="element-402">
            <button disabled={role !== 'Super Admin'} className="bg-[#0D3B3B] text-white px-6 py-3.5 rounded-xl font-bold text-sm flex items-center gap-2 hover:bg-[#092D2D] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-emerald-900/20" data-id="element-403">
              <SaveIcon className="w-4 h-4" data-id="element-404" /> Apply New Rate
            </button>
          </div>
        </div>
      </div>

      {/* Rate History Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-id="element-405">
        <div className="px-6 py-5 border-b border-gray-100" data-id="element-406">
          <h3 className="font-bold text-[#0D3B3B]" data-id="element-407">Rate History</h3>
        </div>
        <div className="overflow-x-auto" data-id="element-408">
          <table className="w-full text-left" data-id="element-409">
            <thead data-id="element-410">
              <tr className="bg-gray-50/50 text-xs uppercase tracking-wider text-gray-500 font-semibold" data-id="element-411">
                <th className="px-6 py-4" data-id="element-412">Rate</th>
                <th className="px-6 py-4" data-id="element-413">Benchmark</th>
                <th className="px-6 py-4" data-id="element-414">Effective Period</th>
                <th className="px-6 py-4" data-id="element-415">Updated By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-416">
              {rateHistory.map(history => <tr key={history.id} className="hover:bg-gray-50" data-id="element-417">
                  <td className="px-6 py-4 text-sm font-black text-[#0D3B3B]" data-id="element-418">
                    {history.rate}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600" data-id="element-419">
                    {history.benchmark}
                  </td>
                  <td className="px-6 py-4" data-id="element-420">
                    <div className="text-sm font-bold text-[#0D3B3B]" data-id="element-421">
                      {history.effectiveFrom}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-422">
                      to {history.effectiveTo}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600" data-id="element-423">
                    {history.updatedBy}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}